import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Form, useFormikContext } from 'formik';
import AppTextField from '@crema/core/AppFormComponents/AppTextField';
import { useAuthUser } from '@crema/utility/AuthHooks';
import Api from '@api';
import axios from 'axios';

const GLOBAL_ROLES = ['SUPERADMIN', 'TANENTADMIN'];

const ChangePasswordForm = ({ onCancel }) => {
  const { isSubmitting, values } = useFormikContext();
  const { user } = useAuthUser();
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showRetype, setShowRetype] = useState(false);
  const [policy, setPolicy] = useState(null);

  const userRole = user?.userRole || user?.userList?.userRole || '';

  // SUPERADMIN and TANENTADMIN are not tied to any corporate —
  // they use the platform-level "SYSTEM" policy instead.
  const policyKey = useMemo(() => {
    if (GLOBAL_ROLES.includes(userRole)) return 'SYSTEM';
    return user?.userList?.corporateId || user?.corporateId || '';
  }, [user, userRole]);

  useEffect(() => {
    if (!policyKey) return;
    axios
      .get(`${Api.auth.getPasswordPolicy}/${policyKey}`)
      .then((res) => {
        const status = res?.data?.status;
        if ((status === 200 || status === '200') && res?.data?.data) {
          setPolicy(res.data.data);
        }
      })
      .catch(() => {});
  }, [policyKey]);

  const pwd = values?.newPassword || '';
  const confirmPwd = values?.retypeNewPassword || '';

  const rules = policy
    ? [
        policy.minimumLength != null && {
          label: `At least ${policy.minimumLength} characters`,
          met: pwd.length >= policy.minimumLength,
        },
        policy.maximumLength != null && {
          label: `No more than ${policy.maximumLength} characters`,
          met: pwd.length > 0 && pwd.length <= policy.maximumLength,
        },
        policy.upperCaseCharacter && {
          label: 'At least 1 uppercase letter (A–Z)',
          met: /[A-Z]/.test(pwd),
        },
        policy.lowerCaseCharacter && {
          label: 'At least 1 lowercase letter (a–z)',
          met: /[a-z]/.test(pwd),
        },
        policy.numericCharacter && {
          label: 'At least 1 number (0–9)',
          met: /\d/.test(pwd),
        },
        policy.specialCharacter && {
          label: 'At least 1 special character (!@#$...)',
          met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
        },
      ].filter(Boolean)
    : [];

  const passwordsMatch = pwd.length > 0 && confirmPwd.length > 0 && pwd === confirmPwd;
  const allRulesMet = rules.length > 0 ? rules.every((r) => r.met) : pwd.length >= 8;
  const canSubmit = !isSubmitting && allRulesMet && passwordsMatch && (values.oldPassword?.length > 0);

  return (
    <Form autoComplete='off'>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#EAF4FD',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LockOutlined sx={{ fontSize: 30, color: '#0A8FDC' }} />
        </Box>
      </Box>

      <Typography sx={{ textAlign: 'center', color: 'grey.500', fontSize: 13, mb: 3 }}>
        Keep your account secure with a strong password
      </Typography>

      <Box sx={{ mb: 2.5 }}>
        <AppTextField
          fullWidth
          type={showOld ? 'text' : 'password'}
          name='oldPassword'
          label='Current Password'
          variant='outlined'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowOld((v) => !v)} edge='end'>
                  {showOld ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
        />
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <AppTextField
          fullWidth
          type={showNew ? 'text' : 'password'}
          name='newPassword'
          label='New Password'
          variant='outlined'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowNew((v) => !v)} edge='end'>
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
        />
      </Box>

      {/* Policy rules checklist — shown below New Password as user types */}
      {pwd.length > 0 && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: '#F5F7FE',
            borderRadius: '8px',
            border: '1px solid #e9e9e9',
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'grey.600', mb: 1 }}>
            Password requirements
          </Typography>
          {rules.map((rule) => (
            <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {rule.met ? (
                <CheckCircle sx={{ fontSize: 15, color: '#11C15B' }} />
              ) : (
                <RadioButtonUnchecked sx={{ fontSize: 15, color: '#c5c6cb' }} />
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  color: rule.met ? '#11C15B' : 'grey.500',
                  fontWeight: rule.met ? 500 : 400,
                }}
              >
                {rule.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mb: confirmPwd.length > 0 ? 1.5 : 3.5 }}>
        <AppTextField
          fullWidth
          type={showRetype ? 'text' : 'password'}
          name='retypeNewPassword'
          label='Confirm New Password'
          variant='outlined'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowRetype((v) => !v)} edge='end'>
                  {showRetype ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
        />
      </Box>

      {/* Passwords match indicator — shown below Confirm Password */}
      {confirmPwd.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, px: 0.5 }}>
          {passwordsMatch ? (
            <CheckCircle sx={{ fontSize: 15, color: '#11C15B' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 15, color: '#c5c6cb' }} />
          )}
          <Typography sx={{ fontSize: 12, color: passwordsMatch ? '#11C15B' : 'grey.500', fontWeight: passwordsMatch ? 500 : 400 }}>
            Passwords match
          </Typography>
        </Box>
      )}

      <Button
        id='btnMui123'
        fullWidth
        variant='contained'
        color='primary'
        type='submit'
        disabled={!canSubmit}
        sx={{
          py: 1.25,
          fontSize: 15,
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '8px',
          mb: 1.5,
        }}
      >
        {isSubmitting ? 'Updating...' : 'Update Password'}
      </Button>

      {onCancel && (
        <Button
          fullWidth
          variant='text'
          onClick={onCancel}
          sx={{ fontSize: 14, textTransform: 'none', color: 'grey.500' }}
        >
          Cancel
        </Button>
      )}
    </Form>
  );
};

export default ChangePasswordForm;
